import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AttachMoney, Save } from '@mui/icons-material';
import adminService from '../services/adminService';

const FEE_PAYER_LABELS = {
  platform: 'Platform',
  contractor: 'Contractor',
};

const CADENCE_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  manual: 'Manual',
};

const AdminVerifierSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ verificationFee: '', feePayer: '', payoutCadence: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.getVerifierSettings();
      setSettings(data);
      setDraft({
        verificationFee: String(data.verificationFee),
        feePayer: data.feePayer,
        payoutCadence: data.payoutCadence,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load verifier settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isDirty = settings && (
    String(draft.verificationFee) !== String(settings.verificationFee) ||
    draft.feePayer !== settings.feePayer ||
    draft.payoutCadence !== settings.payoutCadence
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const feeVal = parseFloat(draft.verificationFee);
      if (!isFinite(feeVal) || feeVal < 0) {
        setError('Verification fee must be a number >= 0');
        setSaving(false);
        return;
      }
      const res = await adminService.updateVerifierSettings({
        verificationFee: feeVal,
        feePayer: draft.feePayer,
        payoutCadence: draft.payoutCadence,
      });
      setSuccess(res.message || 'Settings saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AttachMoney color="primary" />
        <Typography variant="h4">Verifier Payment Settings</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Configure the verification fee charged per completed field verification, who pays the fee, and how often verifiers are paid out.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            {/* Verification fee */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Verification fee (USD)</Typography>
              <TextField
                fullWidth
                type="number"
                value={draft.verificationFee}
                onChange={(e) => setDraft((prev) => ({ ...prev, verificationFee: e.target.value }))}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="The fee charged for each completed field verification."
              />
            </Box>

            <Divider />

            {/* Who pays the fee */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Who pays the verification fee</Typography>
              <FormControl fullWidth>
                <InputLabel id="fee-payer-label">Fee payer</InputLabel>
                <Select
                  labelId="fee-payer-label"
                  value={draft.feePayer}
                  label="Fee payer"
                  onChange={(e) => setDraft((prev) => ({ ...prev, feePayer: e.target.value }))}
                >
                  {Object.entries(FEE_PAYER_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Platform: the fee is absorbed by Eqaya. Contractor: the fee is deducted from the contractor's payment.
              </Typography>
            </Box>

            <Divider />

            {/* Payout cadence */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Payout cadence</Typography>
              <FormControl fullWidth>
                <InputLabel id="cadence-label">Payout cadence</InputLabel>
                <Select
                  labelId="cadence-label"
                  value={draft.payoutCadence}
                  label="Payout cadence"
                  onChange={(e) => setDraft((prev) => ({ ...prev, payoutCadence: e.target.value }))}
                >
                  {Object.entries(CADENCE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                How frequently earned verifier fees are paid out. Manual means payouts are triggered by an admin.
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                disabled={!isDirty || saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminVerifierSettingsPage;
